<!--
  Flowbite tables component
  https://flowbite.com/docs/forms/input-field/
-->
<template>
  <input
    :id="id"
    ref="inputText"
    v-model.trim="inputValue"
    :class="mergedClassObject"
    :type="type"
    :autocomplete="autocomplete"
    :autofocus="autofocus"
    :disabled="disabled"
    :name="name"
    :placeholder="placeholder"
    :readonly="readonly"
    :required="required"
    :size="size"
    :inputmode="inputmode"
    :step="step"
    :maxlength="maxlength"
    :pattern="pattern"
    @keyup.enter="$emit('on-keyup-enter')"
    @keyup="$emit('keyup')"
    @click="onlyNumberAndDash ? handleClick() : ''"
    @focus="onlyNumberAndDash ? handleFocus() : ''"
    @blur="onlyNumberAndDash ? handleBlur() : ''"
    @input="$emit('input', $event.target.value)"
  />
</template>

<script>
export default {
  name: 'InputText',
  props: {
    id: {
      type: String,
      default: () => {
        return Math.random().toString(36).replace('0.', 'id_')
      },
    },
    classObject: {
      type: Object,
      default: () => ({}),
    },
    type: {
      type: [String, Number],
      default: 'text',
    },
    autocomplete: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    required: {
      type: String,
      default: '',
    },
    size: {
      type: [String, Number],
      default: '',
    },
    applyHalfWidthText: {
      type: Boolean,
      default: true,
    },
    inputmode: {
      type: String,
      default: '',
    },
    step: {
      type: Number,
      default: null,
    },
    onlyNumberFlg: {
      type: Boolean,
      default: false,
    },
    onlyNumberAndDash: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: Number,
      default: null,
    },
    pattern: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      inputValue: this.value,
      mergedClassObject: {},
      defaultClassObject: {
        border: true,
        'border-gray-300': true,
        'text-gray-700': true,
        'text-sm': true,
        rounded: true,
        block: true,
        'w-full': true,
        'p-2.5': true,
        'fcs-input': true,
      },
      disabledClassObject: {
        'bg-gray-200': true,
        'text-gray-500': true,
        'cursor-not-allowed': true,
      },
    }
  },
  watch: {
    value(value) {
      if (value !== this.inputValue) {
        this.inputValue = value
      }
    },
    inputValue: function (val) {
      // 入力値の数字と記号を半角に置換
      if (this.applyHalfWidthText && val) {
        this.inputValue = val
          .toString()
          .replace(/[Ａ-Ｚａ-ｚ０-９！＂＃＄％＆＇（）＊＋，－．／：；＜＝＞？＠［＼］＾＿｀｛｜｝]/g, function (s) {
            return String.fromCharCode(s.charCodeAt(0) - 0xfee0)
          })
          .replace(/[‐－―−]/g, '-')
          .replace(/[～〜]/g, '~')
      }

      // 数字、カンマ以外の文字を削除
      if (this.onlyNumberFlg) {
        const regex = /^[0-9]*$/
        if (!regex.test(val)) {
          this.inputValue = val?.toString().replace(/[^0-9.]/g, '')
        }
      }

      // onlyNumberAndDashがtrueの時
      // 入力内容が半角数字と「-」のみ
      // テキストフィールドが空の場合、「0」が自動的にセット
      if (this.onlyNumberAndDash) {
        // 0から9までの数字、カンマのみを許容する正規表現
        const regex = /^[0-9,]*$/
        if (!regex.test(val)) {
          // 数字、カンマ以外の文字を削除
          this.inputValue = val.replace(/[^0-9,]/g, '')
        }

        // リアルタイムでお金の表示に変換
        if (val) {
          this.inputValue = this.$heplerMember.convertToYenWithoutUnit(val)
        }
      }

      // memo: このコンポーネントの変更を親コンポーネント側に同期するために、親コンポーネント側で v-model:value でバインディングする必要あり
      this.$emit('update:value', this.inputValue)
    },
    disabled: {
      immediate: true,
      handler(newVal) {
        this.updateClassObject(newVal)
      },
    },
  },
  created() {
    this.updateClassObject(this.disabled)
  },
  methods: {
    updateClassObject(isDisabled) {
      this.mergedClassObject = Object.assign(
        {},
        this.defaultClassObject,
        isDisabled ? this.disabledClassObject : {},
        JSON.parse(JSON.stringify(this.classObject || {}))
      )
    },
    getCursorPosition() {
      return this.$refs.inputText.selectionStart
    },
    // 最初にクリックした時、値を空にする
    handleClick() {
      if (Number(this.inputValue) === 0) {
        this.inputValue = ''
      }
    },
    // 入力値が0の場合、テキストフィールドをクリア
    handleFocus() {
      if (this.inputValue === '0') {
        this.inputValue = ''
      }
    },
    // フォーカスが離れた時に、テキストフィールドが空の場合、'0'をセット
    handleBlur() {
      if (this.inputValue.trim() === '') {
        this.inputValue = '0'
      }
    },
  },
}
</script>

<style scoped lang="scss">
.fcs-input {
  outline: none;
  box-shadow: none;
  border-color: grey;
}

.border-gray-300 {
  border-color: #d1d5db;
}
</style>
